import { Flex, Text, Textarea } from "@mantine/core";
import React, { useRef } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import Hint from "@/shared/ui/hint";
import BasicButton from "@/UI/BasicButton";
import { TNewOrderForm } from "@/entities/new-order";
import { throwNewToastError } from "@/Utils/MainUtils";
import { getDatesBetween } from "@/shared/helpers";
import { dateListsCheck } from "@/features/new-order/country-item/lib";
import { getKeysFromCountry } from "@/entities/new-order/utils";

type TKeywordsAdditionProps = {
  countryIndex: number;
  withViews?: boolean;
};

export const KeywordsAddition = ({
  countryIndex,
  withViews,
}: TKeywordsAdditionProps) => {
  const { control, watch, setError } = useFormContext<TNewOrderForm>();
  const { update } = useFieldArray({
    control,
    name: "countriesList",
  });

  const { dateStart, dateStop, countriesList } = watch();

  const country = countriesList[countryIndex];

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const getKeywordsFromTextarea = (textareaValue: string): string[] => {
    const lines = textareaValue.split("\n");
    const keywords: string[] = [];

    lines.forEach((line) => {
      const trimmedLine = line.trim().toLowerCase();

      if (trimmedLine !== "") {
        const splitKeywords = trimmedLine
          .split(",")
          .map((keyword) => keyword.trim())
          .filter((keyword) => keyword !== "");
        keywords.push(...splitKeywords);
      }
    });

    return keywords;
  };

  const handleAddNewKeywords = () => {
    if (!dateStart || !dateStop) {
      throwNewToastError("Please select start and end date");
      setError("dateStart", { message: "Set start date" });
      setError("dateStop", { message: "Set stop date" });
      return;
    }

    if (!textAreaRef.current || !textAreaRef.current.value) {
      throwNewToastError("Enter at least one keyword");
      return;
    }

    const newKeywords = getKeywordsFromTextarea(textAreaRef.current.value);

    const dateArray = getDatesBetween(dateStart, dateStop);

    let newCountryKeywords = JSON.parse(JSON.stringify(country.keywords));

    if (dateListsCheck(country, dateArray)) {
      dateArray.forEach((date) => {
        if (!newCountryKeywords[date]) {
          newCountryKeywords[date] = {};
        }
        newKeywords.forEach((key) => {
          if (!withViews) {
            newCountryKeywords[date][key] = 0;
          } else {
            newCountryKeywords[date][key] = {
              installs: 0,
              views: 0,
              CR: 100,
            };
          }
        });
      });
    } else {
      const oldKeys = getKeysFromCountry(country);
      const allKeysArray = [...oldKeys, ...newKeywords];

      newCountryKeywords = {};

      dateArray.forEach((date) => {
        if (!newCountryKeywords[date]) {
          newCountryKeywords[date] = {};
        }
        allKeysArray.forEach((key) => {
          if (!withViews) {
            newCountryKeywords[date][key] = 0;
          } else {
            newCountryKeywords[date][key] = {
              installs: 0,
              views: 0,
              CR: 100,
            };
          }
        });
      });
    }

    if (textAreaRef.current) {
      textAreaRef.current.value = "";
    }

    update(countryIndex, { ...country, keywords: newCountryKeywords });
  };

  return (
    <Flex gap="lg">
      <Flex gap="md">
        <Flex gap={8} w={139} py="md" px={0} justify="flex-end">
          <Text>Keywords</Text>
          <Hint popupText="Enter keywords. Each individual keyword must be on a new line or separated by commas." />
        </Flex>
        <Textarea ref={textAreaRef} rows={4} w={256} size="md" resize="vertical"/>
      </Flex>
      <BasicButton
        onClick={handleAddNewKeywords}
        size="md"
        text="Save keywords"
        variant="blue"
      />
    </Flex>
  );
};
